/**
 * Entity and Device Type Color Utilities
 * 
 * Provides consistent, theme-aware colors for entity types (Student, RP, SRP, External)
 * and device types (Watch, Vehicle, Phone) throughout the application.
 * 
 * These colors are designed to work with the theme system and respect the user's
 * selected color scheme while maintaining visual consistency for categorical data.
 */

import { useTheme } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';

// Entity type definitions
export type EntityType = 'Student' | 'RP' | 'SRP' | 'External' | 'Unknown';
export type DeviceType = 'Watch' | 'Vehicle' | 'Phone' | 'Unknown';

// Entity type display info
export interface EntityTypeDisplay {
  icon: string;
  color: string;
  label: string;
}

// Device type display info
export interface DeviceTypeDisplay {
  color: string;
  label: string;
}

/**
 * Get entity type colors from theme
 * Maps entity types to semantic theme colors:
 * - Student: primary (main brand color)
 * - RP: warning (orange tones)
 * - SRP: secondary (secondary brand color)
 * - External/GeoNeutron: info (informational/purple tones)
 */
export const getEntityTypeColors = (theme: Theme) => ({
  Student: theme.palette.primary.main,
  RP: theme.palette.warning.main,
  SRP: theme.palette.secondary.main,
  External: theme.palette.info.main,
  Unknown: theme.palette.grey[500],
});

/**
 * Get device type colors from theme
 * Maps device types to semantic theme colors:
 * - Watch: info (blue tones)
 * - Vehicle: secondary (purple tones)
 * - Phone: success (green tones)
 */
export const getDeviceTypeColors = (theme: Theme) => ({
  Watch: theme.palette.info.main,
  Vehicle: theme.palette.secondary.main,
  Phone: theme.palette.success.main,
  Unknown: theme.palette.grey[500],
});

/**
 * Get status colors from theme
 * For UI elements like "saved", "loading", "has results"
 */
export const getStatusColors = (theme: Theme) => ({
  saved: theme.palette.success.main,
  loading: theme.palette.warning.main,
  hasResults: theme.palette.warning.main,
  noResults: theme.palette.primary.main,
  active: theme.palette.success.main,
  inactive: theme.palette.grey[500],
  error: theme.palette.error.main,
});

/**
 * Get full entity type display info (icon, color, label)
 */
export const getEntityTypeDisplay = (entityType: string, theme: Theme): EntityTypeDisplay => {
  const colors = getEntityTypeColors(theme);
  
  switch (entityType) {
    case 'SRP':
      return { icon: '🟠', color: colors.SRP, label: 'SRP' };
    case 'RP':
      return { icon: '🟢', color: colors.RP, label: 'RP' };
    case 'Student':
      return { icon: '🔵', color: colors.Student, label: 'Student' };
    case 'External':
      return { icon: '🌍', color: colors.External, label: 'GeoNeutron' };
    default:
      return { icon: '❓', color: colors.Unknown, label: 'Unknown' };
  }
};

/**
 * Get device type color
 */
export const getDeviceTypeColor = (deviceType: string, theme: Theme): string => {
  const colors = getDeviceTypeColors(theme);
  
  switch (deviceType?.toLowerCase()) {
    case 'watch':
      return colors.Watch;
    case 'vehicle':
      return colors.Vehicle;
    case 'phone':
      return colors.Phone;
    default:
      return colors.Unknown;
  }
};

/**
 * Hook to get entity type colors
 * Usage: const entityColors = useEntityTypeColors();
 */
export const useEntityTypeColors = () => {
  const theme = useTheme();
  return getEntityTypeColors(theme);
};

/**
 * Hook to get device type colors
 * Usage: const deviceColors = useDeviceTypeColors();
 */
export const useDeviceTypeColors = () => {
  const theme = useTheme();
  return getDeviceTypeColors(theme);
};

/**
 * Hook to get status colors
 * Usage: const statusColors = useStatusColors();
 */
export const useStatusColors = () => {
  const theme = useTheme();
  return getStatusColors(theme);
};

/**
 * Hook to get entity type display (icon, color, label)
 * Usage: const display = useEntityTypeDisplay('Student');
 */
export const useEntityTypeDisplay = (entityType: string): EntityTypeDisplay => {
  const theme = useTheme();
  return getEntityTypeDisplay(entityType, theme);
};

/**
 * Hook to get device type color
 * Usage: const color = useDeviceTypeColor('Watch');
 */
export const useDeviceTypeColor = (deviceType: string): string => {
  const theme = useTheme();
  return getDeviceTypeColor(deviceType, theme);
};

/**
 * Generate a consistent, vibrant color for a device based on its ID.
 * Uses hashing to ensure the same device always gets the same color.
 * Colors are optimized for visibility on dark map backgrounds.
 * 
 * @param deviceId - The unique device identifier
 * @returns HSL color string
 */
export const generateDeviceColor = (deviceId: string): string => {
  // Hash the device ID to get a consistent number
  let hash = 0;
  for (let i = 0; i < deviceId.length; i++) {
    hash = deviceId.charCodeAt(i) + ((hash << 5) - hash);
    hash = hash & hash; // Convert to 32bit integer
  }
  
  // Generate vibrant HSL color
  const hue = Math.abs(hash % 360); // Full hue range for maximum variety
  const saturation = 70 + (Math.abs(hash) % 30); // 70-100% saturation for vibrant colors
  const lightness = 50 + (Math.abs(hash >> 8) % 20); // 50-70% lightness for visibility on dark maps
  
  return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
};